import React from 'react';
import { Link, Route } from 'react-router-dom';
import { compose, withStateHandlers } from 'recompose';
import PropTypes from 'prop-types';

import connect from 'app/libs/connect';
import noop from 'app/libs/noop';
import Text from 'app/components/layout/Text.styled';
import { getLocaleMap } from 'app/features/localization/selectors';
import { languageSelected } from 'app/features/localization/actions';
import localizeLink from 'app/features/localization/localizeLink';
import { userLocale } from 'app/features/user/selectors';
import S from 'app/components/LanguageSelector/LanguageSelector.styled';
import withExternalClick from 'app/enhancers/withExternalClick';
import { DEFAULT_LOCALE } from 'app/features/localization/constants';

const LanguageSelector = React.forwardRef(
  (
    { userLocale, localeMap, isOpen, onSelectorClick, onLanguageSelect },
    ref,
  ) => (
    <Route>
      {({ location }) => (
        <S.Selector
          isOpened={isOpen}
          className={isOpen && 'opened'}
          onClick={onSelectorClick}
          data-t="open_language_dropdown"
          ref={ref}
        >
          <Text size="l" type="semibold" as={S.Selector.Language}>
            {localeMap[userLocale]}
          </Text>
          <S.Selector.Icon
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            data-t="chevron-right-svg"
          >
            <path d="M8.6 7.4L10 6l6 6-6 6-1.4-1.4 4.6-4.6z" />
          </S.Selector.Icon>
          <S.Selector.LanguageList>
            {Object.entries(localeMap).map(([locale, label]) => (
              <li key={locale}>
                <Link
                  to={{
                    ...location,
                    pathname: `${localizeLink(location.pathname, locale)}`,
                  }}
                  onClick={() => onLanguageSelect(locale)}
                >
                  <Text size="l" type="semibold" as={S.Selector.LanguageItem}>
                    {label}&#x200E;
                  </Text>
                </Link>
              </li>
            ))}
          </S.Selector.LanguageList>
        </S.Selector>
      )}
    </Route>
  ),
);

LanguageSelector.propTypes = {
  localeMap: PropTypes.objectOf(PropTypes.string).isRequired,
  userLocale: PropTypes.string,
  isOpen: PropTypes.bool,
  onSelectorClick: PropTypes.func,
  onLanguageSelect: PropTypes.func,
};

LanguageSelector.defaultProps = {
  userLocale: DEFAULT_LOCALE,
  isOpen: false,
  onSelectorClick: noop,
  onLanguageSelect: noop,
};

const state = { userLocale, localeMap: getLocaleMap };
const actions = { onLanguageSelect: languageSelected };

export { LanguageSelector };

export default compose(
  connect(state, actions),
  withStateHandlers(
    { isOpen: false },
    {
      onSelectorClick:
        ({ isOpen }) =>
        () => ({ isOpen: !isOpen }),
      handleClose: () => () => ({ isOpen: false }),
    },
  ),
  withExternalClick,
)(LanguageSelector);
